import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { useCurrencies, useAccountSettings } from "@/entities/settings/hooks";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import { map } from "lodash";
import IconContainer from "@/components/ui/icon-container";
import { Check, ChevronDown } from "@dreamhub/designsystem/icons";
import SearchInput from "@/components/ui/search-input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

interface CurrencyDropdownProps {
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  disabledTooltipText?: string;
}

const CurrencyDropdown: React.FC<CurrencyDropdownProps> = ({
  value,
  onChange,
  disabled = false,
  disabledTooltipText
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setSearchValue("");
    }
  }, [disabled]);

  const currencies = useCurrencies().data ?? [];
  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;

  const accountCurrency = useMemo(() => {
    if (accountSettings && accountSettings.length > 0) {
      const keyValuePairs = extractKeyValuePairs(accountSettings);
      return keyValuePairs?.account_currency || "USD";
    }
    return "USD";
  }, [accountSettings]);

  const selectedValue = value ?? accountCurrency;

  const currencyOptions = useMemo(() => {
    return map(currencies, (currencyCode) => ({
      id: currencyCode,
      displayName: `${getCurrencySymbol(currencyCode)} ${currencyCode}`
    }));
  }, [currencies]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return currencyOptions;
    const lowerSearch = searchValue.toLowerCase();
    return currencyOptions.filter(
      (option) =>
        option.id.toLowerCase().includes(lowerSearch) ||
        option.displayName.toLowerCase().includes(lowerSearch)
    );
  }, [currencyOptions, searchValue]);

  const handleSelection = (currencyCode: string) => {
    if (disabled) return;
    onChange?.(currencyCode);
    setIsOpen(false);
    setSearchValue("");
  };

  const displayValue = `${selectedValue}`;

  const dropdown = (
    <div
      className={cn(
        // Height pinned to match the sibling action button beside it. The
        // hairline is an inset ring, not a border: a 0.5px border resolves to
        // 1px of layout per side, which left both inner segments at 30px.
        "inline-flex h-8 items-center rounded-lg ring-[0.5px] ring-inset ring-border-badge-default bg-badges-default",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className="flex items-center self-stretch px-[6px] border-r-[0.5px] border-border-badge-default">
        <Text variant="bodySecondary">
          {t("contractTerms.contractCurrency")}
        </Text>
      </div>
      <Popover open={isOpen} onOpenChange={disabled ? undefined : setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "h-full py-1 px-[6px] border-none rounded-none rounded-r-lg",
              isOpen && "bg-surface-focus-selected"
            )}
            variant="secondaryNoBg"
            disabled={disabled}
          >
            <Text variant="body" className="text-primary">
              {displayValue}
            </Text>
            {/* The control was already clickable; this is the affordance that says so. */}
            <IconContainer
              as="span"
              size="5"
              color="secondary"
              className={cn(
                "transition-transform duration-200 ease-in-out",
                isOpen && "rotate-180"
              )}
            >
              <ChevronDown />
            </IconContainer>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          padding={4}
          className="w-48 max-h-[300px] gap-1"
          align="end"
          sideOffset={4}
        >
          <div className="flex flex-col gap-2">
            <div className="sticky top-0 bg-surface-elevation2 z-10">
              <SearchInput
                placeholder={t("search")}
                value={searchValue}
                onChange={setSearchValue}
                hideIcon
                clearButtonSize="xs"
                clearButtonColor="secondary"
              />
            </div>
            <div className="flex flex-col overflow-y-auto max-h-[220px]">
              {filteredOptions.map((option) => {
                const isSelected = option.id === selectedValue;
                return (
                  <div
                    key={option.id}
                    onClick={() => handleSelection(option.id)}
                    className={cn(
                      "flex items-center justify-between px-2 py-2 cursor-pointer rounded-lg",
                      "hover:bg-surface-hover-soft active:bg-surface-pressed",
                      "transition-all duration-200 ease-in-out",
                      isSelected && "bg-surface-hover-soft"
                    )}
                  >
                    <Text variant="body">{option.displayName}</Text>
                    {isSelected && (
                      <IconContainer className="text-cta">
                        <Check />
                      </IconContainer>
                    )}
                  </div>
                );
              })}
              {filteredOptions.length === 0 && (
                <Text variant="bodyTertiary" className="px-2 py-4 text-center">
                  {t("common.noResults", "No results found")}
                </Text>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  if (disabled && disabledTooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{dropdown}</TooltipTrigger>
        <TooltipContent>{disabledTooltipText}</TooltipContent>
      </Tooltip>
    );
  }
  return dropdown;
};

export default CurrencyDropdown;
